import { create } from 'zustand';
import api from './authStore';

export interface GameLevel {
  _id: string;
  levelNumber: number;
  name: string;
  description: string;
  gridSize: { width: number; height: number };
  gemTypes: number;
  moveLimit?: number;
  timeLimit?: number;
  goals: Array<{
    type: 'score' | 'destroy' | 'collect' | 'drop';
    targetType?: string;
    targetCount: number;
  }>;
  obstacles?: Array<{
    type: 'ice' | 'block' | 'locked' | 'empty';
    positions: Array<{ x: number; y: number }>;
  }>;
  availableBoosters: string[];
  parScore: number;
  goldScore: number;
  perfectScore: number;
  isActive: boolean;
}

export interface LevelProgress {
  _id: string;
  levelNumber: number;
  stars: number;
  bestScore: number;
  completed: boolean;
  attempts: number;
}

interface LevelState {
  levels: GameLevel[];
  progress: LevelProgress[];
  currentLevel: GameLevel | null;
  isLoading: boolean;
  error: string | null;
  totalLevels: number;
  hasMore: boolean;

  fetchLevels: (page?: number) => Promise<void>;
  loadMoreLevels: () => Promise<void>;
  fetchProgress: () => Promise<void>;
  fetchLevel: (levelNumber: number) => Promise<GameLevel | null>;
  submitLevelResult: (levelNumber: number, score: number, completed: boolean) => Promise<void>;
  setCurrentLevel: (level: GameLevel | null) => void;
}

export const useLevelStore = create<LevelState>((set, get) => ({
  levels: [],
  progress: [],
  currentLevel: null,
  isLoading: false,
  error: null,
  totalLevels: 0,
  hasMore: false,

  fetchLevels: async (page = 1) => {
    set({ isLoading: true, error: null, levels: [] });
    try {
      const { data } = await api.get('/levels', { params: { page, limit: 50 } });
      set({
        levels: data.levels,
        totalLevels: data.pagination?.total || 0,
        hasMore: data.pagination?.hasMore || false,
        isLoading: false,
      });
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to fetch levels', isLoading: false });
    }
  },

  loadMoreLevels: async () => {
    if (!get().hasMore || get().isLoading) return;
    const nextPage = Math.ceil(get().levels.length / 50) + 1;
    set({ isLoading: true });
    try {
      const { data } = await api.get('/levels', { params: { page: nextPage, limit: 50 } });
      set({
        levels: [...get().levels, ...data.levels],
        totalLevels: data.pagination?.total || get().totalLevels,
        hasMore: data.pagination?.hasMore || false,
        isLoading: false,
      });
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to fetch more levels', isLoading: false });
    }
  },

  fetchProgress: async () => {
    try {
      const { data } = await api.get('/progress');
      set({ progress: data.progress });
    } catch (error: any) {
      console.error('Failed to fetch progress:', error);
    }
  },

  fetchLevel: async (levelNumber: number) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await api.get(`/levels/${levelNumber}`);
      set({ currentLevel: data.level, isLoading: false });
      return data.level;
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to fetch level', isLoading: false });
      return null;
    }
  },

  submitLevelResult: async (levelNumber: number, score: number, completed: boolean) => {
    try {
      const { data } = await api.post('/progress/submit', { levelNumber, score, completed });
      await get().fetchProgress();
      return data;
    } catch (error: any) {
      console.error('Failed to submit result:', error);
      throw error;
    }
  },

  setCurrentLevel: (level: GameLevel | null) => {
    set({ currentLevel: level });
  }
}));
